import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader.js';
import { useTenantContext } from '../api/tenant-context.js';
import {
  archiveConnector,
  createConnector,
  listConnectors,
  testConnector,
  updateConnector,
  type ConnectorPatch,
  type ConnectorProvider,
  type OutboundConnector,
  type TestConnectorResult,
} from '../api/connectors.js';
import { getProviderWebhook, setProviderWebhook } from '../api/providerWebhook.js';
import { listChannels } from '../api/channels.js';
import {
  archiveCcConnector,
  createCcConnector,
  listCcConnectors,
  updateCcConnector,
  type CcConnector,
  type CcConnectorPatch,
} from '../api/ccConnectors.js';

type Tab = 'd360' | 'meta' | 'five9';

const TAB_META: Record<Tab, { label: string; helper: string }> = {
  d360: {
    label: 'Dialog360',
    helper: 'Conectores con provider=360dialog (BSP de WhatsApp).',
  },
  meta: {
    label: 'Meta · WhatsApp API',
    helper: 'Conectores con provider=meta_native (WhatsApp Business API).',
  },
  five9: {
    label: 'Five9',
    helper: 'Channels tipo five9 para handoff a agentes humanos.',
  },
};

export function Conexiones(): JSX.Element {
  const [tab, setTab] = useState<Tab>('d360');
  const { tenant } = useTenantContext();
  const tenantId = tenant?.id ?? '';
  const qc = useQueryClient();

  const connectorsQ = useQuery({
    queryKey: ['connectors', tenantId],
    queryFn: () => listConnectors(tenantId),
    enabled: !!tenantId,
  });
  const channelsQ = useQuery({
    queryKey: ['channels', tenantId],
    queryFn: () => listChannels(tenantId),
    enabled: !!tenantId,
  });
  // #156 — Five9 ahora usa CcConnector (auth anon), no Channel.
  const ccQ = useQuery({
    queryKey: ['cc-connectors', tenantId],
    queryFn: () => listCcConnectors(tenantId),
    enabled: !!tenantId,
  });

  const connectors = connectorsQ.data ?? [];
  // channels query se mantiene para validar reachability/loader (legacy); no se filtra Five9 acá.
  void channelsQ.data;
  const ccConnectors = ccQ.data ?? [];
  const d360 = connectors.filter((c) => c.provider === '360dialog' && c.deletedAt === null);
  const meta = connectors.filter((c) => c.provider === 'meta_native' && c.deletedAt === null);
  const five9 = ccConnectors.filter((c) => c.deletedAt === null);

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['connectors', tenantId] });
    void qc.invalidateQueries({ queryKey: ['channels', tenantId] });
    void qc.invalidateQueries({ queryKey: ['cc-connectors', tenantId] });
  };

  return (
    <>
      <PageHeader
        title="Conexiones"
        subtitle="Configura y monitorea las tres integraciones principales del workspace."
      />

      <div className="flex gap-1 mb-2" role="tablist">
        <TabBtn active={tab === 'd360'} onClick={() => setTab('d360')} dotOk={d360.length > 0}>
          Dialog360 <span className="ml-1 text-ink-4">{d360.length}</span>
        </TabBtn>
        <TabBtn active={tab === 'meta'} onClick={() => setTab('meta')} dotOk={meta.length > 0}>
          Meta · WhatsApp API <span className="ml-1 text-ink-4">{meta.length}</span>
        </TabBtn>
        <TabBtn active={tab === 'five9'} onClick={() => setTab('five9')} dotOk={five9.length > 0}>
          Five9 <span className="ml-1 text-ink-4">{five9.length}</span>
        </TabBtn>
      </div>
      <div className="text-[12px] text-ink-4 mb-4">{TAB_META[tab].helper}</div>

      {tab === 'd360' && (
        <ConnectorsPanel tenantId={tenantId} provider="360dialog" items={d360} onChanged={refresh} />
      )}
      {tab === 'meta' && (
        <ConnectorsPanel tenantId={tenantId} provider="meta_native" items={meta} onChanged={refresh} />
      )}
      {tab === 'five9' && <Five9Panel tenantId={tenantId} items={five9} onChanged={refresh} />}
    </>
  );
}

function TabBtn(props: { active: boolean; onClick: () => void; children: React.ReactNode; dotOk: boolean }): JSX.Element {
  return (
    <button
      type="button"
      className={`tab ${props.active ? 'active' : ''}`}
      onClick={props.onClick}
      role="tab"
      aria-selected={props.active}
    >
      <span className={`dot ${props.dotOk ? 'dot-ok' : 'dot-idle'}`} />
      {props.children}
    </button>
  );
}

// ── Connectors panel (Dialog360 / Meta) ────────────────────────────────────

function ConnectorsPanel({
  tenantId,
  provider,
  items,
  onChanged,
}: {
  tenantId: string;
  provider: ConnectorProvider;
  items: OutboundConnector[];
  onChanged: () => void;
}): JSX.Element {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<OutboundConnector | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" onClick={() => setCreating((v) => !v)} className="btn-primary">
          {creating ? 'Cancelar' : '+ Agregar conexión'}
        </button>
      </div>

      {creating && (
        <ConnectorForm
          tenantId={tenantId}
          provider={provider}
          mode="create"
          onSuccess={() => {
            setCreating(false);
            onChanged();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {items.length === 0 && !creating && (
        <div className="panel text-ink-3 text-sm">
          Sin conexiones {provider === '360dialog' ? 'Dialog360' : 'Meta'} configuradas.
        </div>
      )}

      {items.map((c) =>
        editing === c.id ? (
          <ConnectorForm
            key={c.id}
            tenantId={tenantId}
            provider={provider}
            mode="edit"
            initial={c}
            onSuccess={() => {
              setEditing(null);
              onChanged();
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <ConnectorCard
            key={c.id}
            tenantId={tenantId}
            connector={c}
            onEdit={() => setEditing(c.id)}
            onArchive={() => setConfirmArchive(c)}
          />
        ),
      )}

      {confirmArchive && (
        <ConfirmModal
          title="¿Archivar conexión?"
          desc={`Se archivará "${confirmArchive.name}". Acción reversible vía soporte.`}
          confirmLabel="Archivar"
          onConfirm={async () => {
            await archiveConnector(tenantId, confirmArchive.id);
            setConfirmArchive(null);
            onChanged();
          }}
          onCancel={() => setConfirmArchive(null)}
        />
      )}
    </div>
  );
}

function ConnectorCard({
  tenantId,
  connector,
  onEdit,
  onArchive,
}: {
  tenantId: string;
  connector: OutboundConnector;
  onEdit: () => void;
  onArchive: () => void;
}): JSX.Element {
  const c = connector;
  // #160 — Test connection inline.
  const [testResult, setTestResult] = useState<TestConnectorResult | null>(null);
  const testMut = useMutation({
    mutationFn: () => testConnector(tenantId, c.id),
    onSuccess: (res) => setTestResult(res),
    onError: (e: unknown) => setTestResult({ ok: false, provider: c.provider, error: (e as Error).message }),
  });
  const supportsTest = c.provider === 'meta_native' || c.provider === '360dialog';
  return (
    <div className="panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[14px] font-semibold">{c.name}</div>
          <div className="text-[12px] text-ink-3 mt-0.5">Proveedor: {c.provider}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`badge ${c.status === 'connected' ? 'badge-ok' : 'badge-err'}`}>
            <span className={`dot ${c.status === 'connected' ? 'dot-ok' : 'dot-err'}`} />
            {c.status === 'connected' ? 'Conectado' : 'Error'}
          </span>
          {supportsTest && c.apiKeySecretArn && (
            <button
              type="button"
              onClick={() => testMut.mutate()}
              disabled={testMut.isPending}
              className="btn-mini"
              title="Probar credencial contra el provider"
            >
              {testMut.isPending ? 'Probando…' : 'Probar'}
            </button>
          )}
          <button type="button" onClick={onEdit} className="btn-mini">Editar</button>
          <button type="button" onClick={onArchive} className="btn-mini btn-mini-danger">Archivar</button>
        </div>
      </div>
      <DefRow label="Phone Number ID" value={c.phoneNumberId ?? '—'} mono />
      <DefRow label="Número visible" value={c.displayPhoneNumber ?? '—'} mono />
      <DefRow label="Nombre verificado" value={c.verifiedName ?? '—'} />
      {c.wabaId && <DefRow label="WABA ID" value={c.wabaId} mono />}
      {c.apiKeySecretArn && <DefRow label="Credencial" value="✓ Provisionada (Secret SM)" />}
      <DefRow label="Versión" value={`v${c.version}`} />
      {testResult && (
        <div
          className={`mt-2 rounded px-2 py-1 text-[12px] ${
            testResult.ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
          role="status"
        >
          {testResult.ok
            ? `✓ Conexión OK · ${testResult.templatesCount ?? 0} templates`
            : `✗ ${testResult.error ?? 'Falló'}`}
        </div>
      )}
    </div>
  );
}

function ConnectorForm({
  tenantId,
  provider,
  mode,
  initial,
  onSuccess,
  onCancel,
}: {
  tenantId: string;
  provider: ConnectorProvider;
  mode: 'create' | 'edit';
  initial?: OutboundConnector;
  onSuccess: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [name, setName] = useState(initial?.name ?? '');
  const [businessPhone, setBusinessPhone] = useState(initial?.businessPhone ?? '');
  const [wabaId, setWabaId] = useState(initial?.wabaId ?? '');
  const [phoneNumberId, setPhoneNumberId] = useState(initial?.phoneNumberId ?? '');
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState(initial?.displayPhoneNumber ?? '');
  const [verifiedName, setVerifiedName] = useState(initial?.verifiedName ?? '');
  // #159: credencial plain (write-only). En edit arranca vacío; si se completa rota.
  const [credential, setCredential] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const isMeta = provider === 'meta_native';

  const mut = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Nombre es obligatorio');
      if (mode === 'create' && (!credential || credential.length < 8)) {
        throw new Error(isMeta
          ? 'Access Token requerido (mínimo 8 chars)'
          : 'API Key requerida (mínimo 8 chars)');
      }
      if (credential && (credential.length < 8 || credential.length > 4096)) {
        throw new Error('Credencial debe tener entre 8 y 4096 chars');
      }
      const payload: Record<string, unknown> = {
        name: name.trim(),
        ...(businessPhone.trim() ? { businessPhone: businessPhone.trim() } : {}),
        ...(wabaId.trim() ? { wabaId: wabaId.trim() } : {}),
        ...(phoneNumberId.trim() ? { phoneNumberId: phoneNumberId.trim() } : {}),
        ...(displayPhoneNumber.trim() ? { displayPhoneNumber: displayPhoneNumber.trim() } : {}),
        ...(verifiedName.trim() ? { verifiedName: verifiedName.trim() } : {}),
        // #159: en create siempre va; en edit solo si se completó.
        ...(credential ? (isMeta ? { accessToken: credential } : { apiKey: credential }) : {}),
      };
      if (mode === 'create') {
        return createConnector(tenantId, { ...payload, provider } as unknown as Parameters<typeof createConnector>[1]);
      }
      if (initial) {
        const patch = payload as unknown as ConnectorPatch;
        return updateConnector(tenantId, initial.id, patch, initial.version);
      }
      throw new Error('invalid state');
    },
    onSuccess: () => onSuccess(),
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'error'),
  });

  return (
    <div className="panel space-y-3">
      <div className="text-[13px] font-semibold">
        {mode === 'create' ? `Nuevo connector ${provider}` : `Editar ${initial?.name}`}
      </div>
      <Field label="Nombre">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input" maxLength={120} />
      </Field>
      <Field label="Provider" helper="No editable después de creado.">
        <input type="text" value={provider} readOnly className="input mono opacity-60" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Phone Number ID">
          <input type="text" value={phoneNumberId} onChange={(e) => setPhoneNumberId(e.target.value)} className="input mono" />
        </Field>
        <Field label="Número visible">
          <input type="text" value={displayPhoneNumber} onChange={(e) => setDisplayPhoneNumber(e.target.value)} className="input mono" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="WABA ID">
          <input type="text" value={wabaId} onChange={(e) => setWabaId(e.target.value)} className="input mono" />
        </Field>
        <Field label="Nombre verificado">
          <input type="text" value={verifiedName} onChange={(e) => setVerifiedName(e.target.value)} className="input" />
        </Field>
      </div>
      <Field label="Business phone">
        <input type="text" value={businessPhone} onChange={(e) => setBusinessPhone(e.target.value)} className="input mono" />
      </Field>
      <Field
        label={
          isMeta
            ? mode === 'create'
              ? 'Access Token de Meta (write-only)'
              : 'Nuevo Access Token (dejar vacío para no rotar)'
            : mode === 'create'
              ? `API Key del provider (write-only)`
              : 'Nueva API Key (dejar vacío para no rotar)'
        }
        helper={
          mode === 'create'
            ? 'Se guarda como Secret en AWS Secrets Manager (path /zen/dev/connector-secrets/...). Nunca se devuelve al frontend.'
            : 'Si se completa, rota el contenido del Secret. El ARN no cambia.'
        }
      >
        <input
          type="password"
          autoComplete="new-password"
          value={credential}
          onChange={(e) => setCredential(e.target.value)}
          className="input mono"
          placeholder={mode === 'create' ? '••••••••' : 'sin cambio'}
        />
      </Field>
      {/* #165 — Webhook config (solo 360dialog en edit) */}
      {mode === 'edit' && provider === '360dialog' && initial && (
        <D360WebhookPanel tenantId={tenantId} connectorId={initial.id} />
      )}
      {err && <p className="text-[12px] text-err">{err}</p>}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
        <button type="button" onClick={() => mut.mutate()} disabled={mut.isPending} className="btn-primary">
          {mut.isPending ? '…' : mode === 'create' ? 'Crear conexión' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  );
}

function D360WebhookPanel({ tenantId, connectorId }: { tenantId: string; connectorId: string }): JSX.Element {
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['webhook-config', tenantId, connectorId],
    queryFn: () => getProviderWebhook(tenantId, connectorId),
  });
  useEffect(() => {
    if (q.data?.url) setDraft(q.data.url);
  }, [q.data]);

  const mut = useMutation({
    mutationFn: () => setProviderWebhook(tenantId, connectorId, draft.trim()),
    onSuccess: () => {
      setErr(null);
      setOkMsg('Webhook configurado en 360dialog ✓');
      q.refetch();
    },
    onError: (e: unknown) => {
      setOkMsg(null);
      setErr(e instanceof Error ? e.message : String(e));
    },
  });

  return (
    <div className="rounded-[6px] p-3 mt-2" style={{ background: 'var(--bg-2)', border: '1px solid var(--border)' }}>
      <div className="text-[13px] font-semibold mb-1">Webhook 360dialog</div>
      <div className="text-[11.5px] text-ink-3 mb-2">
        URL pública (HTTPS) que recibirá los mensajes inbound. Cambiarla sincroniza inmediato con 360dialog.
      </div>
      {q.isLoading && <p className="text-[11px] text-ink-3">Consultando 360dialog…</p>}
      {q.error && <p className="text-[11px] text-err">{(q.error as Error).message}</p>}
      {!q.isLoading && !q.error && (
        <>
          <div className="text-[11.5px] text-ink-3 mb-2">
            Actual: {q.data?.url ? <span className="mono">{q.data.url}</span> : <span className="italic text-ink-4">sin configurar</span>}
          </div>
          <div className="flex gap-2">
            <input
              type="url"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="https://inbound.dev.zen.zervizdev.com/v1/webhooks/360dialog"
              className="input mono flex-1"
            />
            <button
              type="button"
              onClick={() => mut.mutate()}
              disabled={mut.isPending || !draft.trim() || !draft.startsWith('https://')}
              className="btn-mini"
            >
              {mut.isPending ? 'Guardando…' : 'Guardar webhook'}
            </button>
          </div>
          {okMsg && <p className="mt-1 text-[11px] text-ok">{okMsg}</p>}
          {err && <p className="mt-1 text-[11px] text-err">{err}</p>}
        </>
      )}
    </div>
  );
}

// ── Five9 panel (#156 — CcConnector auth anon) ─────────────────────────────

function Five9Panel({
  tenantId,
  items,
  onChanged,
}: {
  tenantId: string;
  items: CcConnector[];
  onChanged: () => void;
}): JSX.Element {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<CcConnector | null>(null);

  return (
    <div className="space-y-4">
      <div className="panel">
        <div className="text-[13px] font-semibold mb-2">¿Cómo se configura Five9?</div>
        <ol className="list-decimal list-inside text-[12.5px] text-ink-3 space-y-1">
          <li>
            Five9 Digital usa <strong>auth anónimo cookieless</strong>: no se requiere
            apiKey ni password. La autorización la hace Five9 vía IP allowlist del lado suyo
            (pedir a soporte Five9 habilitar nuestras IPs egress).
          </li>
          <li>
            Configurá los campos abajo: <code className="mono">Auth Base URL</code>,
            {' '}<code className="mono">Tenant Five9</code>, <code className="mono">Campaign</code>
            {' '}y <code className="mono">interactionTaskKey</code>.
          </li>
          <li>
            En tus flujos, agregá un step <code className="mono">handoff</code> con{' '}
            <code className="mono">target=five9-digital</code> para enrutar a Five9. El runtime
            hará auth anon contra Five9 con cache de token (TTL 90s).
          </li>
        </ol>
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={() => setCreating((v) => !v)} className="btn-primary">
          {creating ? 'Cancelar' : '+ Agregar connector Five9'}
        </button>
      </div>

      {creating && (
        <Five9Form
          tenantId={tenantId}
          mode="create"
          onSuccess={() => {
            setCreating(false);
            onChanged();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {items.length === 0 && !creating && (
        <div className="panel text-ink-3 text-sm">Sin connectors Five9 configurados.</div>
      )}

      {items.map((c) =>
        editing === c.id ? (
          <Five9Form
            key={c.id}
            tenantId={tenantId}
            mode="edit"
            initial={c}
            onSuccess={() => {
              setEditing(null);
              onChanged();
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <Five9Card
            key={c.id}
            connector={c}
            onEdit={() => setEditing(c.id)}
            onArchive={() => setConfirmArchive(c)}
          />
        ),
      )}

      {confirmArchive && (
        <ConfirmModal
          title="¿Archivar connector Five9?"
          desc={`Se archivará "${confirmArchive.name}". Los flujos handoff fallarán hasta crear otro.`}
          confirmLabel="Archivar"
          onConfirm={async () => {
            await archiveCcConnector(tenantId, confirmArchive.id, confirmArchive.version);
            setConfirmArchive(null);
            onChanged();
          }}
          onCancel={() => setConfirmArchive(null)}
        />
      )}
    </div>
  );
}

function Five9Card({
  connector,
  onEdit,
  onArchive,
}: {
  connector: CcConnector;
  onEdit: () => void;
  onArchive: () => void;
}): JSX.Element {
  const c = connector;
  return (
    <div className="panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[14px] font-semibold">{c.name}</div>
          <div className="text-[12px] text-ink-3 mt-0.5">Five9 Digital · auth anon</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`badge ${c.status === 'connected' ? 'badge-ok' : 'badge-err'}`}>
            <span className={`dot ${c.status === 'connected' ? 'dot-ok' : 'dot-err'}`} />
            {c.status}
          </span>
          <button type="button" onClick={onEdit} className="btn-mini">Editar</button>
          <button type="button" onClick={onArchive} className="btn-mini btn-mini-danger">Archivar</button>
        </div>
      </div>
      <DefRow label="Auth Base URL" value={c.authBaseUrl} mono />
      {c.messagesBaseUrl && <DefRow label="Messages Base URL" value={c.messagesBaseUrl} mono />}
      <DefRow label="Tenant Five9" value={c.tenantName} />
      <DefRow label="Campaign" value={c.campaign} />
      <DefRow label="interactionTaskKey" value={c.interactionTaskKey} mono />
      <DefRow label="Versión" value={`v${c.version}`} />
    </div>
  );
}

function Five9Form({
  tenantId,
  mode,
  initial,
  onSuccess,
  onCancel,
}: {
  tenantId: string;
  mode: 'create' | 'edit';
  initial?: CcConnector;
  onSuccess: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [name, setName] = useState(initial?.name ?? '');
  const [authBaseUrl, setAuthBaseUrl] = useState(initial?.authBaseUrl ?? 'https://app.five9.com');
  const [messagesBaseUrl, setMessagesBaseUrl] = useState(
    initial?.messagesBaseUrl ?? 'https://app-atl.five9.com:443',
  );
  const [tenantName, setTenantName] = useState(initial?.tenantName ?? '');
  const [campaign, setCampaign] = useState(initial?.campaign ?? '');
  const [interactionTaskKey, setInteractionTaskKey] = useState(initial?.interactionTaskKey ?? '');
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Nombre es obligatorio');
      if (!/^https:\/\//.test(authBaseUrl)) throw new Error('Auth Base URL debe ser HTTPS');
      if (messagesBaseUrl && !/^https:\/\//.test(messagesBaseUrl))
        throw new Error('Messages Base URL debe ser HTTPS');
      if (!tenantName.trim()) throw new Error('Tenant Five9 es obligatorio');
      if (!campaign.trim()) throw new Error('Campaign es obligatorio');
      if (!interactionTaskKey.trim()) throw new Error('interactionTaskKey es obligatorio');

      if (mode === 'create') {
        return createCcConnector(tenantId, {
          name: name.trim(),
          provider: 'five9_digital',
          authBaseUrl: authBaseUrl.trim(),
          ...(messagesBaseUrl.trim() ? { messagesBaseUrl: messagesBaseUrl.trim() } : {}),
          tenantName: tenantName.trim(),
          campaign: campaign.trim(),
          interactionTaskKey: interactionTaskKey.trim(),
        });
      }
      if (initial) {
        const patch: CcConnectorPatch = { expectedVersion: initial.version };
        if (name.trim() !== initial.name) patch.name = name.trim();
        if (authBaseUrl.trim() !== initial.authBaseUrl) patch.authBaseUrl = authBaseUrl.trim();
        const incomingMsg = messagesBaseUrl.trim();
        if (incomingMsg !== (initial.messagesBaseUrl ?? '')) patch.messagesBaseUrl = incomingMsg;
        if (tenantName.trim() !== initial.tenantName) patch.tenantName = tenantName.trim();
        if (campaign.trim() !== initial.campaign) patch.campaign = campaign.trim();
        if (interactionTaskKey.trim() !== initial.interactionTaskKey)
          patch.interactionTaskKey = interactionTaskKey.trim();
        return updateCcConnector(tenantId, initial.id, patch);
      }
      throw new Error('invalid');
    },
    onSuccess: () => onSuccess(),
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'error'),
  });

  return (
    <div className="panel space-y-3">
      <div className="text-[13px] font-semibold">
        {mode === 'create' ? 'Nuevo connector Five9' : `Editar ${initial?.name}`}
      </div>
      <Field label="Nombre">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input"
          maxLength={120}
          placeholder="Five9 Producción"
        />
      </Field>
      <Field
        label="Auth Base URL"
        helper="Endpoint del auth anon. Ej: https://app.five9.com"
      >
        <input
          type="text"
          value={authBaseUrl}
          onChange={(e) => setAuthBaseUrl(e.target.value)}
          className="input mono"
        />
      </Field>
      <Field
        label="Messages Base URL (opcional)"
        helper="Override del apiHost devuelto por el auth. Útil cuando la farm es estable."
      >
        <input
          type="text"
          value={messagesBaseUrl}
          onChange={(e) => setMessagesBaseUrl(e.target.value)}
          className="input mono"
          placeholder="https://app-atl.five9.com:443"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Tenant Five9 (tenantName)">
          <input
            type="text"
            value={tenantName}
            onChange={(e) => setTenantName(e.target.value)}
            className="input"
            placeholder="Zerviz"
          />
        </Field>
        <Field label="Campaign">
          <input
            type="text"
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            className="input"
            placeholder="Chat_Bot_Handoff"
          />
        </Field>
      </div>
      <Field label="interactionTaskKey">
        <input
          type="text"
          value={interactionTaskKey}
          onChange={(e) => setInteractionTaskKey(e.target.value)}
          className="input mono"
        />
      </Field>
      {err && <p className="text-[12px] text-err">{err}</p>}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
        <button type="button" onClick={() => mut.mutate()} disabled={mut.isPending} className="btn-primary">
          {mut.isPending ? '…' : mode === 'create' ? 'Crear connector' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

// ── Shared ─────────────────────────────────────────────────────────────────

function ConfirmModal({
  title,
  desc,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  desc: string;
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-lg border border-border-2 bg-surface p-5">
        <div className="text-[15px] font-semibold mb-2">{title}</div>
        <p className="text-[13px] text-ink-3 mb-4">{desc}</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            className="btn-primary"
            style={{ background: 'var(--err)' }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  helper,
  children,
}: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="block mb-1 text-[12px] text-ink-3">{label}</span>
      {children}
      {helper && <span className="block mt-1 text-[10.5px] text-ink-4">{helper}</span>}
    </label>
  );
}

function DefRow({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex gap-4 py-2 border-b border-border last:border-b-0">
      <div className="text-[12px] text-ink-4 w-[180px] shrink-0">{label}</div>
      <div className={`text-[13px] ${mono ? 'mono' : ''} text-ink-2 break-all`}>{value}</div>
    </div>
  );
}
